import { joinIds, splitIds, updateWithToast, updateWithToastPromise } from '@/clientOnlyUtils/clientUtils';
import { AnimatePresence } from 'framer-motion';
import React from 'react';
import { toast } from 'react-hot-toast';

import { TeamUserAvatar } from '@poseidon/components/Avatar';
import { ESC_PRIORITY, useEscStack } from '@poseidon/components/contexts';
import {
  Button,
  ButtonGroup,
  type CampaignOverrideMode,
  CampaignTemplateFilterSelectWithOverride,
  FilterSelect,
  OwnerFilterSelect,
  StageFilterSelect,
} from '@poseidon/components/Input2';
import { DeleteCadencePopup, MarkDonePopup } from '@poseidon/components/Popup';
import { Arrows, General, Media, Users } from '@poseidon/components/StrokeIcons';
import { WaveSpinner } from '@poseidon/components/WaveSpinner';
import { hasItems } from '@poseidon/shared-utils';

import { useAiStepBulkCreateMutation } from '@/hooks/trpc/useAiStepMutation';
import {
  useBulkChangeCadenceMutation,
  useCadenceChangeUserBulkMutation,
  useCadenceDeleteAllMutation,
  useCadencePauseAllMutation,
  useCadenceResumeAllMutation,
} from '@/hooks/trpc/useCadenceMutation';
import {
  useContactBulkAckInteraction,
  useContactBulkSetStageMutation,
  useEnhanceContactMutation,
  useResearchFromLinkedInMutation,
} from '@/hooks/trpc/useContactMutation';
import { useGlobalReminderDeleteBulk } from '@/hooks/trpc/useGlobalReminders';
import { useBulkAddTagToContactsMutation } from '@/hooks/trpc/useTagMutation';
import { useUpdateTeamData } from '@/hooks/trpc/useUserMutation';
import { useRouterQuery } from '@/hooks/useRouterQuery';
import { api } from '@/utils/api';
import { useAuthContext } from '~/auth/auth.context';
import { TagFilterSelectNextJS } from '~/components/TagFilterSelectNextJS';
import SelectPopupContainer from '~/components/SelectedPopup/SelectPopupContainer';
import { SkippedContactsPopup } from '~/components/SkippedContactsPopup';
import { useIsAdminLike } from '~/hooks/useSettingsPermissions';

type ContactSelectedPopupProps = {
  teamId: string;
  userId: string;
  isPremium: boolean;
  creditsRemaining: number;
};

export const ContactSelectedPopup = (props: ContactSelectedPopupProps) => {
  const [query] = useRouterQuery();
  const [skippedContactPublicIds, setSkippedContactPublicIds] = React.useState<string[]>([]);

  const aiStepBulkCreateMutation = useAiStepBulkCreateMutation();

  const onAddSkippedAnyway = (contactPublicIds: string[]) =>
    aiStepBulkCreateMutation.mutateAsync({
      teamId: props.teamId,
      userId: props.userId,
      data: { contactIds: contactPublicIds, type: 'message' },
      onlyNewPeople: false,
    });

  return (
    <>
      <AnimatePresence>
        {hasItems(query.selectedIds) && (
          <ContactSelectedPopupWithIds
            {...props}
            selectedIds={query.selectedIds ?? []}
            onSkipped={setSkippedContactPublicIds}
          />
        )}
      </AnimatePresence>
      {skippedContactPublicIds.length > 0 && (
        <SkippedContactsPopup
          teamId={props.teamId}
          skippedContactPublicIds={skippedContactPublicIds}
          onClose={() => setSkippedContactPublicIds([])}
          onAddAnyway={onAddSkippedAnyway}
        />
      )}
    </>
  );
};

interface ContactSelectedPopupWithIdsProps extends ContactSelectedPopupProps {
  selectedIds: string[];
  onSkipped: (contactPublicIds: string[]) => void;
}
export const ContactSelectedPopupWithIds = ({
  teamId,
  userId,
  isPremium,
  creditsRemaining,
  selectedIds,
  onSkipped,
}: ContactSelectedPopupWithIdsProps) => {
  const [loading, setLoading] = React.useState<string>();
  const [shouldDeleteReminders, setShouldDeleteReminders] = React.useState(false);
  const [shouldResolveEvents, setShouldResolveEvents] = React.useState(false);
  const [shouldDeleteCadences, setShouldDeleteCadences] = React.useState(false);

  const [auth] = useAuthContext();
  const authedUserTeam = 'userTeam' in auth ? auth.userTeam : null;
  const onlyNewPeople = authedUserTeam?.takeActionOnlyNewPeople ?? true;
  const campaignOverrideMode: CampaignOverrideMode = authedUserTeam?.campaignOverrideMode ?? 'smart_override';

  const utils = api.useUtils();
  const updateTeamDataMutation = useUpdateTeamData();
  const setOnlyNewPeople = (next: boolean) => {
    if (next === onlyNewPeople) return;
    // Optimistically flip the chevron — feels instant. The shared
    // useUpdateTeamData hook invalidates user.me on success (which
    // reconciles against the server) and we rollback on error.
    const prev = utils.user.me.getData();
    utils.user.me.setData(undefined, (old) => {
      if (!old || !('userTeam' in old) || !old.userTeam) return old;
      return { ...old, userTeam: { ...old.userTeam, takeActionOnlyNewPeople: next } };
    });
    updateTeamDataMutation.mutate(
      { teamId, userId, data: { takeActionOnlyNewPeople: next } },
      {
        onError: () => {
          if (prev) utils.user.me.setData(undefined, prev);
        },
      },
    );
  };
  const setCampaignOverrideMode = (next: CampaignOverrideMode) => {
    if (next === campaignOverrideMode) return;
    const prev = utils.user.me.getData();
    utils.user.me.setData(undefined, (old) => {
      if (!old || !('userTeam' in old) || !old.userTeam) return old;
      return { ...old, userTeam: { ...old.userTeam, campaignOverrideMode: next } };
    });
    updateTeamDataMutation.mutate(
      { teamId, userId, data: { campaignOverrideMode: next } },
      {
        onError: () => {
          if (prev) utils.user.me.setData(undefined, prev);
        },
      },
    );
  };

  const isAdminLike = useIsAdminLike();
  const teamUserOptionsQuery = api.team.teamUserOptions.useQuery({ teamId }, { enabled: isAdminLike });
  const userOptions = teamUserOptionsQuery.data ?? [];

  const cadenceTemplatesQuery = api.cadenceTemplate.namesWithOwner.useQuery({ teamId, pagination: { limit: 5000 } });
  const templates =
    cadenceTemplatesQuery.data?.data.map((d) => ({
      id: d.id,
      name: d.name,
      isMine: d.userId === userId,
      errorCount: d.errorSteps?.length ?? 0,
      ownerName: d.ownerName,
      ownerProfilePicture: d.ownerProfilePicture,
    })) ?? [];

  const stagesQuery = api.stage.all.useQuery({ teamId });
  const stages = stagesQuery.data?.stages ?? [];

  const [query, setQuery] = useRouterQuery();
  const enhanceMutation = useEnhanceContactMutation();
  const researchMutation = useResearchFromLinkedInMutation();
  const bulkChangeCadenceMutation = useBulkChangeCadenceMutation();
  const cadenceDeleteAllMutation = useCadenceDeleteAllMutation();
  const cadenceChangeUserMutation = useCadenceChangeUserBulkMutation();
  const cadencePauseAllMutation = useCadencePauseAllMutation();
  const cadenceResumeAllMutation = useCadenceResumeAllMutation();
  const contactBulkAckInteractionMutation = useContactBulkAckInteraction();
  const deleteRemindersMutation = useGlobalReminderDeleteBulk();
  const aiStepBulkCreateMutation = useAiStepBulkCreateMutation();
  const bulkSetStageMutation = useContactBulkSetStageMutation();
  const bulkAddTagMutation = useBulkAddTagToContactsMutation();

  const splitSelectedIds = selectedIds.map((c) => splitIds(c));

  const onClose = () => setQuery((q) => ({ ...q, selectedIds: undefined }));
  useEscStack('ContactSelectedPopup', ESC_PRIORITY.ACTION_BAR, onClose);

  const onDelete = async () => {
    const idObj = splitIds(query?.id ?? '');
    const didSelectId = splitSelectedIds.find((s) => s.cadenceId == idObj.cadenceId) != null;

    setShouldDeleteCadences(false);
    await updateWithToast({
      loading: 'Deleting...',
      success: 'Cadences deleted',
      setQuery,
      setLoading,
      onSuccessQuery:
        idObj && didSelectId
          ? { selectedIds: undefined, id: joinIds({ contactId: idObj.contactId }) }
          : { selectedIds: undefined },
      onErrorQuery: { selectedIds: undefined },
      mutationFnAsync: () =>
        cadenceDeleteAllMutation.mutateAsync({
          teamId: teamId,
          cadenceIds: splitSelectedIds.map((c) => c.cadenceId).filter((a) => a != null),
        }),
    });
  };

  const onChangeOwner = (userId: string) =>
    updateWithToast({
      loading: 'Changing Owner...',
      success: userId === '' ? 'Owner removed' : 'Owner changed',
      setQuery,
      setLoading,
      onSuccessQuery: { selectedIds: undefined },
      onErrorQuery: { selectedIds: undefined },
      mutationFnAsync: () =>
        cadenceChangeUserMutation.mutateAsync({
          data: {
            cadenceIds: splitSelectedIds.map((c) => c.cadenceId).filter((a) => a != null),
            userId: userId || null,
          },
        }),
    });

  const onEnhance = () => {
    const contactIds = selectedIds.map((c) => splitIds(c).contactId).filter((a) => a != null);
    const willEnrich = isPremium && creditsRemaining > 0;

    return updateWithToast({
      loading: willEnrich ? 'Refreshing & enriching...' : 'Refreshing from LinkedIn...',
      success: willEnrich ? 'Contacts refreshed & enriched' : 'Contacts refreshed from LinkedIn',
      setQuery,
      setLoading,
      onSuccessQuery: { selectedIds: undefined },
      onErrorQuery: { selectedIds: undefined },
      mutationFnAsync: async () => {
        const researchResults = await Promise.all(
          contactIds.map((contactId) =>
            researchMutation
              .mutateAsync({ contactId, teamId: teamId, userId: userId })
              .then((r) => {
                return { ok: true as const, contactId, r };
              })
              .catch((e: unknown) => {
                return { ok: false as const, contactId, error: e };
              }),
          ),
        );

        const experienceThrottledCount = researchResults.filter(
          (res) => res.ok && res.r?.throttledSections?.includes('experience'),
        ).length;
        // Empty-without-throttle is a different condition (truly private / no job history).
        const emptyExperienceCount = researchResults.filter(
          (res) =>
            res.ok &&
            res.r?.experienceFetchedCount === 0 &&
            !res.r?.throttledSections?.includes('experience') &&
            !res.r?.skipped,
        ).length;
        const skippedFreshCount = researchResults.filter((res) => res.ok && res.r?.skipped).length;

        if (experienceThrottledCount > 0) {
          toast(
            experienceThrottledCount === 1
              ? "LinkedIn is currently rate-limiting this profile's experience section. We'll automatically re-fetch in a few minutes — your existing experiences are preserved."
              : `LinkedIn is currently rate-limiting the experience section for ${experienceThrottledCount} contacts. We'll automatically re-fetch in a few minutes — existing experiences are preserved.`,
            { duration: 8000 },
          );
        } else if (emptyExperienceCount > 0) {
          toast(
            emptyExperienceCount === 1
              ? 'LinkedIn returned no work history for 1 contact (private profile or no public job history). Existing experiences were kept.'
              : `LinkedIn returned no work history for ${emptyExperienceCount} contacts (private profiles or no public job history). Existing experiences were kept.`,
            { duration: 6000 },
          );
        } else if (skippedFreshCount > 0) {
          toast(
            skippedFreshCount === 1
              ? 'Contact was refreshed in the last 24h — skipped re-fetching.'
              : `${skippedFreshCount} contacts were refreshed in the last 24h — skipped re-fetching.`,
            { duration: 5000 },
          );
        }
        if (willEnrich) {
          await Promise.all(
            contactIds.map((contactId) =>
              enhanceMutation
                .mutateAsync({ contactId })
                .then((r) => {
                  return { ok: true as const, contactId, r };
                })
                .catch((e: unknown) => {
                  return { ok: false as const, contactId, error: e };
                }),
            ),
          );
        }
      },
    });
  };

  const onBulkStartCadence = async (cadenceTemplateId: string) => {
    const isRemoving = cadenceTemplateId === '';
    const contactIds = splitSelectedIds.map((c) => c.contactId).filter((a) => a != null);

    if (isRemoving) {
      return updateWithToast({
        loading: 'Removing from campaign...',
        success: 'Removed from campaign',
        setQuery,
        setLoading,
        onSuccessQuery: { selectedIds: undefined },
        onErrorQuery: { selectedIds: undefined },
        mutationFnAsync: () =>
          bulkChangeCadenceMutation.mutateAsync({
            teamId,
            cadenceTemplateId: null,
            contactIds,
          }),
      });
    }

    await updateWithToastPromise({
      loading: 'Changing cadence...',
      success: 'Cadences changed',
      mutationFnAsync: () =>
        bulkChangeCadenceMutation.mutateAsync({
          teamId,
          cadenceTemplateId,
          contactIds,
        }),
    });

    setQuery((q) => ({ ...q, selectedIds: undefined }));
  };

  const onBulkSetStage = (stageId: string) =>
    updateWithToast({
      loading: 'Changing stage...',
      success: 'Stage updated',
      setQuery,
      setLoading,
      onSuccessQuery: { selectedIds: undefined },
      onErrorQuery: { selectedIds: undefined },
      mutationFnAsync: () =>
        bulkSetStageMutation.mutateAsync({
          contactIds: splitSelectedIds.map((c) => c.contactId).filter((a) => a != null),
          stageId: stageId || null,
        }),
    });

  const onBulkAddTag = (tagId: string) =>
    updateWithToast({
      loading: 'Adding tag...',
      success: 'Tag added',
      setQuery,
      setLoading,
      onSuccessQuery: { selectedIds: undefined },
      onErrorQuery: { selectedIds: undefined },
      mutationFnAsync: () =>
        bulkAddTagMutation.mutateAsync({
          contactIds: splitSelectedIds.map((c) => c.contactId).filter((a) => a != null),
          tagId,
        }),
    });

  const onResume = async () => {
    await updateWithToast({
      loading: 'Resuming...',
      success: 'Cadences resumed',
      setQuery,
      setLoading,
      onSuccessQuery: { selectedIds: undefined },
      onErrorQuery: { selectedIds: undefined },
      mutationFnAsync: () =>
        cadenceResumeAllMutation.mutateAsync({
          cadenceIds: splitSelectedIds.map((c) => c.cadenceId).filter((a) => a != null),
        }),
    });
  };

  const onPause = () =>
    updateWithToast({
      loading: 'Pausing...',
      success: 'Cadences paused',
      setQuery,
      setLoading,
      onSuccessQuery: { selectedIds: undefined },
      onErrorQuery: { selectedIds: undefined },
      mutationFnAsync: () =>
        cadencePauseAllMutation.mutateAsync({
          cadenceIds: splitSelectedIds.map((c) => c.cadenceId).filter((a) => a != null),
        }),
    });

  const onAddToAiMessage = async () => {
    const contactIds = splitSelectedIds.map((c) => c.contactId).filter((a) => a != null);
    setQuery((q) => ({ ...q, selectedIds: undefined }));
    const toastId = toast.loading('Adding to Take Action...');
    try {
      const result = await aiStepBulkCreateMutation.mutateAsync({
        teamId: teamId,
        userId: userId,
        data: { contactIds, type: 'message' },
        onlyNewPeople,
      });
      // When previously-added contacts were skipped, the SkippedContactsPopup
      // tells the full story — don't double up with a toast.
      if (result.skippedContactIds.length > 0) {
        toast.dismiss(toastId);
        onSkipped(result.skippedContactIds);
      } else {
        const skippedReasons: string[] = [];
        if (result.skippedDisqualified > 0) {
          skippedReasons.push(`${result.skippedDisqualified} disqualified`);
        }
        if (result.skippedAlreadyInQueue > 0) {
          skippedReasons.push(`${result.skippedAlreadyInQueue} already in queue`);
        }
        const skippedMessage = skippedReasons.length > 0 ? `. Skipped: ${skippedReasons.join(', ')}` : '';
        toast.success(`Added ${result.created} to Take Action${skippedMessage}`, { id: toastId });
      }
    } catch {
      toast.error('Failed to add to Take Action', { id: toastId });
    }
  };

  const onAcknowledge = async () => {
    setShouldResolveEvents(false);
    await updateWithToast({
      loading: 'Acknowledging...',
      success: 'Acknowledged views',
      setQuery,
      onSuccessQuery: { selectedIds: undefined },
      onErrorQuery: { selectedIds: undefined },
      mutationFnAsync: () =>
        contactBulkAckInteractionMutation.mutateAsync({
          viewIds: splitSelectedIds.map((c) => c.interactionId).filter((a) => a != null),
        }),
    });
  };

  const onDeleteReminders = async () => {
    setShouldDeleteReminders(false);
    await updateWithToast({
      loading: 'Deleting...',
      success: 'Reminders deleted',
      setQuery,
      onSuccessQuery: { selectedIds: undefined },
      onErrorQuery: { selectedIds: undefined },
      mutationFnAsync: () =>
        deleteRemindersMutation.mutateAsync({
          globalReminderIds: splitSelectedIds.map((c) => c.reminderId).filter((a) => a != null),
        }),
    });
  };

  const cadenceIdsLength = splitSelectedIds.filter((a) => a.cadenceId != null).length ?? 0;
  const contactIdsLength = splitSelectedIds.filter((a) => a.contactId != null).length ?? 0;
  const reminderIdsLength = splitSelectedIds.filter((a) => a.reminderId != null).length ?? 0;
  const interactionIdsLength = splitSelectedIds.filter((a) => a.interactionId != null).length ?? 0;

  return (
    <SelectPopupContainer loading={loading}>
      {reminderIdsLength > 0 && (
        <Button size="sm" icon={<General.Check />} onClick={() => setShouldDeleteReminders(true)}>
          Mark Reminder as Done
        </Button>
      )}

      {interactionIdsLength > 0 && (
        <Button size="sm" icon={<General.Eye />} onClick={() => setShouldResolveEvents(true)}>
          Acknowledge
        </Button>
      )}

      {contactIdsLength > 0 && (
        <Button
          size="sm"
          icon={enhanceMutation.isPending || researchMutation.isPending ? <WaveSpinner /> : <Arrows.RefreshCw04 />}
          disabled={enhanceMutation.isPending || researchMutation.isPending}
          onClick={onEnhance}
          tooltip={
            isPremium && creditsRemaining > 0
              ? `Refresh & enrich ${contactIdsLength} ${contactIdsLength === 1 ? 'contact' : 'contacts'} (uses up to ${Math.min(contactIdsLength, creditsRemaining).toLocaleString()} of ${creditsRemaining.toLocaleString()} credits)`
              : `Refresh LinkedIn data only — no enrichment credits remaining`
          }>
          Enhance
        </Button>
      )}

      {cadenceIdsLength > 0 && (
        <ButtonGroup size="sm">
          <Button
            icon={<Media.PlayCircle />}
            onClick={onResume}
            tooltip={`Resume${selectedIds.length > 1 ? ` ${selectedIds.length}` : ''}`}
          />
          <Button
            icon={<Media.PauseCircle />}
            onClick={onPause}
            tooltip={`Pause${selectedIds.length > 1 ? ` ${selectedIds.length}` : ''}`}
          />
        </ButtonGroup>
      )}

      {contactIdsLength > 0 && (
        <ButtonGroup size="sm">
          <FilterSelect
            value={onlyNewPeople ? 'only_new' : 'everyone'}
            options={[
              {
                value: 'only_new',
                label: 'Only new people',
                description: 'Skip contacts you have ever added to Take Action',
                icon: <Users.UserPlus01 />,
              },
              {
                value: 'everyone',
                label: 'Everyone',
                description: 'Add all selected contacts, even ones you previously added',
                icon: <Users.Users01 />,
              },
            ]}
            onChange={(v) => setOnlyNewPeople(v === 'only_new')}
            tooltip={
              onlyNewPeople
                ? 'Only contacts you have never added to Take Action will be added'
                : 'All selected contacts will be added, even ones you previously added to Take Action'
            }
            filterable={false}
            showText={false}
            autoWidth
          />
          <Button onClick={onAddToAiMessage} icon={<General.Zap />} tooltip="Add to Take Action">
            {/* Collapses to just the lightning bolt in a narrow container. */}
            <span className="hidden @[900px]:inline">Add to Take Action</span>
          </Button>
        </ButtonGroup>
      )}

      {cadenceIdsLength > 0 && isAdminLike && (
        <OwnerFilterSelect
          userOptions={userOptions}
          value={undefined}
          onChange={onChangeOwner}
          placeholder="Owner"
          size="sm"
          tooltip="Change owner"
        />
      )}

      {contactIdsLength > 0 && (
        <CampaignTemplateFilterSelectWithOverride
          templates={templates}
          includeRemoveOption
          renderOwnerIcon={({ ownerProfilePicture, ownerName }) => (
            <TeamUserAvatar
              userTeam={{ profilePicture: ownerProfilePicture, userTeamName: ownerName }}
              className="size-full"
              shape="circle"
            />
          )}
          onChange={onBulkStartCadence}
          overrideMode={campaignOverrideMode}
          onOverrideModeChange={setCampaignOverrideMode}
          placeholder="Campaign"
          size="sm"
        />
      )}

      {contactIdsLength > 0 && (
        <ButtonGroup size="sm">
          <StageFilterSelect
            stages={stages}
            includeRemoveOption
            onChange={onBulkSetStage}
            placeholder="Stage"
            showText={false}
          />
          <TagFilterSelectNextJS
            teamId={teamId}
            onChange={onBulkAddTag}
            allowCreate
            contactOnly
            placeholder="Tag"
            showText={false}
          />
        </ButtonGroup>
      )}

      {shouldDeleteCadences && (
        <DeleteCadencePopup onClose={() => setShouldDeleteCadences(false)} onDelete={() => onDelete()} />
      )}
      {shouldResolveEvents && (
        <MarkDonePopup onClose={() => setShouldResolveEvents(false)} onSubmit={() => onAcknowledge()} />
      )}
      {shouldDeleteReminders && (
        <MarkDonePopup onClose={() => setShouldDeleteReminders(false)} onSubmit={() => onDeleteReminders()} />
      )}
    </SelectPopupContainer>
  );
};
